import { useContext, useEffect, useId, useRef, useState } from 'react'
import {
  Link,
  useBeforeUnload,
  useBlocker,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router'
import {
  CalendarBlankIcon,
  ConfirmDeletionModal,
  DetailsCard,
  DetailsLayoutActionContext,
  FolderIcon,
  FlagIcon,
  Mtds,
  ModalDialog,
  PencilSimpleIcon,
  PlanStatusTag,
  ScreenHeader,
  ScreenLayout,
  TopBar,
  AppLayout,
  BuildingIcon,
  ClipboardTextIcon,
  useGetPlanByIdQuery,
  useGetPlanSampleSummaryQuery,
  getParentBreadcrumbs,
  HandshakeIcon,
  TargetIcon,
  useGetContractsQuery,
  useGetLabsQuery,
  useGetOkProgramsQuery,
  useGetPurposesQuery,
  useGetOwnerNameQuery,
  ScreenHeaderProperty,
  SinglePlan,
  toLocaleDateString,
  TrashIcon,
  type UpdatePlan,
  useDeletePlanMutation,
  useErrorToast,
  UserIcon,
  useUpdatePlanMutation,
  buildUpdatePlanBody,
  useAuth
} from 'prove-common'
import TasksTab from '../components/tasks-tab/TasksTab'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { PlansTab } from '../components/plans-tab/PlansTab'
import { CopyPlanModal } from '../components/copy-plan-modal/CopyPlanModal'
import { EmptyState } from '../components/empty-state/EmptyState'
import { AppLogo } from '../components/app-logo/AppLogo'
import CreateTaskModal from '../components/create-task-modal/CreateTaskModal'
import CreatePlanModal from '../components/create-plan-modal/CreatePlanModal'
import {
  resolvePlanWorkspaceId,
  useIsWorkspaceFeatureEnabled
} from '../workspaces/workspaces'
import EditPlanCard from '../components/edit-plan-card/EditPlanCard'
import { ConfirmActivatePlanModal } from '../components/confirm-activate-plan-modal/ConfirmActivatePlanModal'
import { MovePlanModal } from '../components/move-plan-modal/MovePlanModal'
import { PlanStatusMenuItems } from '../components/plan-status-menu-items/PlanStatusMenuItems'
import { countMissingApprovals } from '../utils/countMissingApprovals'
import {
  getPlanIconId,
  getPlanIconOption,
  planIconOptions,
  setPlanIconId,
  type PlanIconId
} from '../plan-icons/planIcons'
import { PlanStatCard } from '../components/plan-stat-card/PlanStatCard'
import { PlanInstructionsTab } from '../components/plan-instructions-tab/PlanInstructionsTab'
import { PlanLegislationTab } from '../components/plan-legislation-tab/PlanLegislationTab'
import { PlanStatisticsTab } from '../components/plan-statistics-tab/PlanStatisticsTab'

type PlanScreenHeaderProps = {
  plan: SinglePlan
  sampleSummary: {
    totalSampleCount: number
    positiveSampleCount: number
  }
  onClickCopy: () => void
  onOpenCreateTaskModal: () => void
  onOpenCreatePlanModal: () => void
}

const PlanScreenHeader = ({
  plan,
  sampleSummary,
  onClickCopy,
  onOpenCreateTaskModal,
  onOpenCreatePlanModal
}: Readonly<PlanScreenHeaderProps>) => {
  const { isAdmin } = useAuth()
  const isWorkspaceFeatureEnabled = useIsWorkspaceFeatureEnabled().enabled
  const detailsAction = useContext(DetailsLayoutActionContext)
  const [isOpenEditModal, setOpenEditModal] = useState(false)
  const [isOpenMovePlanModal, setOpenMovePlanModal] = useState(false)
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false)
  const [isOpenActivateModal, setOpenActivateModal] = useState(false)
  const [selectedIconId, setSelectedIconId] = useState<PlanIconId>(() =>
    getPlanIconId(plan.id)
  )
  const iconMenuId = useId()
  const createMenuId = useId()
  const contextMenuId = useId()
  const updatePlan = useUpdatePlanMutation()
  const navigate = useNavigate()
  const deletePlan = useDeletePlanMutation()
  const statusUpdateLock = useRef(false)
  const showUpdateError = useErrorToast('oppdatere planen')
  const showDeleteError = useErrorToast('slette planen')

  const parentPlanId = plan.parents?.[0]?.id
  const missingApprovalCount = countMissingApprovals(plan)
  const selectedIcon = getPlanIconOption(selectedIconId)
  const SelectedIcon = selectedIcon.icon
  const subSamplesTaken = plan.totalCount
  const samplesTaken = sampleSummary.totalSampleCount
  const positiveSamples = sampleSummary.positiveSampleCount
  const subSamplesToTake = plan.totalQuantity
  const positivePercentage =
    samplesTaken === 0 ? 0 : (positiveSamples / samplesTaken) * 100
  const needToEditPlan = plan.contractId == null && plan.tasks?.length === 0
  const createTaskTooltip = needToEditPlan
    ? 'Husk å knytte planen til en kontrakt for å kunne legge til oppgaver. Dette kan gjøres ved å redigere planen'
    : undefined
  const properties = [
    {
      label: 'Status',
      icon: <FlagIcon aria-hidden />,
      value: <PlanStatusTag status={plan.status} />
    },
    {
      label: 'Frist',
      icon: <CalendarBlankIcon aria-hidden />,
      value: toLocaleDateString(plan.due) ?? 'Ikke angitt'
    }
  ]

  const updatePlanStatus = async (newStatus: UpdatePlan['status']) => {
    if (plan.etag == null || statusUpdateLock.current) return
    statusUpdateLock.current = true
    try {
      await updatePlan.mutateAsync({
        planId: plan.id,
        etag: plan.etag,
        body: buildUpdatePlanBody(plan, { status: newStatus })
      })
    } catch (error) {
      showUpdateError(error)
    } finally {
      statusUpdateLock.current = false
    }
  }

  const handleActivateRequest = () => {
    if (missingApprovalCount > 0) {
      setOpenActivateModal(true)
      return
    }
    void updatePlanStatus('active')
  }

  const handleConfirmActivate = () => {
    setOpenActivateModal(false)
    void updatePlanStatus('active')
  }

  const handleSelectIcon = (iconId: PlanIconId) => {
    setSelectedIconId(iconId)
    setPlanIconId(plan.id, iconId)
  }

  useEffect(() => {
    setSelectedIconId(getPlanIconId(plan.id))
  }, [plan.id])

  const planIconButton = isWorkspaceFeatureEnabled ? (
    <div>
      <Mtds.Button
        aria-label='Velg ikon for plan'
        data-variant='tertiary'
        popoverTarget={iconMenuId}
        className='bg-neutral-surface-tinted hover:bg-neutral-surface-hover'
      >
        <SelectedIcon aria-hidden />
      </Mtds.Button>
      <Mtds.Popover
        as='menu'
        id={iconMenuId}
        aria-label='Velg ikon for plan'
        className='grid-cols-8 gap-1 p-2 [&:popover-open]:grid'
      >
        {planIconOptions.map((option) => {
          const Icon = option.icon

          return (
            <li key={option.id} className='contents'>
              <Mtds.Button
                type='button'
                aria-label={option.label}
                aria-pressed={option.id === selectedIconId}
                popoverTargetAction='hide'
                onClick={() => handleSelectIcon(option.id)}
                className='aspect-square justify-center'
              >
                <Icon aria-hidden />
              </Mtds.Button>
            </li>
          )
        })}
      </Mtds.Popover>
    </div>
  ) : null

  const headerActions = (
    <>
      <Mtds.Button
        data-variant='primary'
        popoverTarget={createMenuId}
        data-arrow
      >
        Opprett
      </Mtds.Button>
      <Mtds.Popover as='menu' id={createMenuId}>
        <li>
          <Mtds.Button
            onClick={onOpenCreatePlanModal}
            disabled={!plan.canCreateChildren}
          >
            Ny plan
          </Mtds.Button>
        </li>
        <li>
          <Mtds.Button
            onClick={onOpenCreateTaskModal}
            disabled={!plan.canCreateChildren || needToEditPlan}
            data-tooltip={createTaskTooltip}
          >
            Ny oppgave
          </Mtds.Button>
        </li>
      </Mtds.Popover>
      <Mtds.Button aria-label='Handlingsmeny' popoverTarget={contextMenuId} />
      <Mtds.Popover as='menu' id={contextMenuId}>
        <li>
          <Mtds.Button
            onClick={() => setOpenEditModal(true)}
            disabled={!plan.canManage}
          >
            <PencilSimpleIcon />
            Rediger
          </Mtds.Button>
        </li>
        <PlanStatusMenuItems
          status={plan.status}
          onClickMove={
            plan.parents.length > 0 && plan.canManage
              ? () => setOpenMovePlanModal(true)
              : undefined
          }
          onClickDuplicate={onClickCopy}
          onClickActivate={plan.canManage ? handleActivateRequest : undefined}
          onClickDeactivate={
            plan.canManage
              ? () => void updatePlanStatus('deactivated')
              : undefined
          }
          onClickSetAsDraft={
            plan.canManage ? () => void updatePlanStatus('draft') : undefined
          }
        />
        <li>
          <Mtds.Button
            data-color='danger'
            onClick={() => setOpenDeleteModal(true)}
            disabled={!isAdmin}
          >
            <TrashIcon />
            Slett
          </Mtds.Button>
        </li>
      </Mtds.Popover>
    </>
  )

  return (
    <>
      <Mtds.Grid data-gap='lg'>
        <Mtds.Flex
          data-align='center'
          data-justify='space-between'
          data-gap={4}
        >
          {planIconButton}
          <Mtds.Flex data-align='center' className='ml-auto shrink-0'>
            {headerActions}
            {detailsAction}
          </Mtds.Flex>
        </Mtds.Flex>
        <DetailsLayoutActionContext.Provider value={null}>
          <ScreenHeader title={plan.description} properties={properties} />
        </DetailsLayoutActionContext.Provider>
        {(!!plan.tasks?.length || !!plan.children?.length) && (
          <Mtds.Grid data-gap='4' data-items='300'>
            <PlanStatCard
              color='success'
              label='Prøver tatt'
              value={subSamplesTaken}
              description={`av ${subSamplesToTake} prøver planlagt`}
              progressValue={subSamplesTaken}
              progressMax={subSamplesToTake}
              to={`/samples?planIds=${plan.id}&tabIndex=0`}
            />
            <PlanStatCard
              color='danger'
              label='Prøver påvist'
              value={positiveSamples}
              description={`av ${samplesTaken} prøver registrert`}
              badge={
                <Mtds.Tag
                  data-color='danger'
                  data-icon='none'
                  className='rounded-full!'
                >
                  {positivePercentage.toLocaleString('nb-NO', {
                    minimumFractionDigits: positiveSamples === 0 ? 0 : 2,
                    maximumFractionDigits: 2
                  })}{' '}
                  %
                </Mtds.Tag>
              }
              progressValue={positiveSamples}
              progressMax={samplesTaken}
              to={`/samples?planIds=${plan.id}&tabIndex=0&result=POSITIVE`}
            />
          </Mtds.Grid>
        )}
      </Mtds.Grid>
      <EditPlanCard
        isOpen={isOpenEditModal}
        setOpen={setOpenEditModal}
        plan={plan}
      />
      {isOpenMovePlanModal && (
        <MovePlanModal plan={plan} isOpen setOpen={setOpenMovePlanModal} />
      )}
      <ConfirmDeletionModal
        title={`Planen «${plan.description ?? plan.id}» vil bli permanent slettet.`}
        isOpen={isOpenDeleteModal}
        setOpen={setOpenDeleteModal}
        isPending={deletePlan.isPending}
        onConfirm={async () => {
          if (plan != null) {
            await deletePlan
              .mutateAsync({ planId: plan.id, etag: plan.etag })
              .then(() => {
                setOpenDeleteModal(false)
                Mtds.toast.success('Planen er slettet')
                const navigateTo = parentPlanId
                  ? `/plans/${parentPlanId}`
                  : '/plans'
                navigate(navigateTo)
              })
              .catch((error) => {
                showDeleteError(error)
              })
          }
        }}
      />
      <ConfirmActivatePlanModal
        open={isOpenActivateModal}
        onClose={() => setOpenActivateModal(false)}
        onConfirm={handleConfirmActivate}
        missingApprovalCount={missingApprovalCount}
      />
    </>
  )
}

export default function PlanScreen() {
  const isWorkspaceFeatureEnabled = useIsWorkspaceFeatureEnabled().enabled
  const { planId } = useParams()
  const [searchParams] = useSearchParams()
  const debugParam = searchParams.get('debug')
  const {
    data: plan,
    isPending: isPendingPlan,
    isError: isErrorPlan
  } = useGetPlanByIdQuery(Number(planId))
  const {
    data: sampleSummary,
    isPending: isPendingSampleSummary,
    isError: isErrorSampleSummary
  } = useGetPlanSampleSummaryQuery(Number(planId))
  const {
    data: contracts,
    isPending: isPendingContracts,
    isError: isErrorContracts
  } = useGetContractsQuery()
  const {
    data: labs,
    isPending: isPendingLabs,
    isError: isErrorLabs
  } = useGetLabsQuery()
  const contract = contracts?.find(
    (contract) => contract.id === plan?.contractId
  )
  const lab = labs?.find((lab) => lab.id === contract?.labId)
  const { data: okPrograms } = useGetOkProgramsQuery()
  const {
    data: purposes,
    isPending: isPendingPurposes,
    isError: isErrorPurposes
  } = useGetPurposesQuery()
  const purpose = purposes?.find((r) => r.id === plan?.purposeId)
  const purposeText =
    purpose?.name ??
    (plan?.purposeId == null ? undefined : `Hensikt ${plan.purposeId}`)
  const okProgram = okPrograms?.find((r) => r.id === plan?.okProgramId)
  const okProgramValue = okProgram ? (
    <>
      <strong>{okProgram.number}</strong> {okProgram.name}
    </>
  ) : undefined
  const { data: ownerName } = useGetOwnerNameQuery(plan?.ownerId ?? undefined)
  const [isModalOpen, setModalOpen] = useState(false)
  const [isCreateTaskModalOpen, setCreateTaskModalOpen] = useState(false)
  const [isCreatePlanModalOpen, setCreatePlanModalOpen] = useState(false)
  const [selectedPlanTab, setSelectedPlanTab] = useState(0)
  const [isInstructionsDirty, setInstructionsDirty] = useState(false)
  const [pendingPlanTab, setPendingPlanTab] = useState<number | null>(null)
  const [instructionEditorVersion, setInstructionEditorVersion] = useState(0)
  const [isDiscardDialogOpen, setDiscardDialogOpen] = useState(false)
  const navigationBlocker = useBlocker(() => isInstructionsDirty)
  useBeforeUnload((event) => {
    if (isInstructionsDirty) {
      event.preventDefault()
      event.returnValue = ''
    }
  })
  const keepEditingInstructions = () => {
    setDiscardDialogOpen(false)
    setPendingPlanTab(null)
    setSelectedPlanTab(1)
    navigationBlocker.reset?.()
  }
  const discardInstructions = () => {
    setInstructionsDirty(false)
    setInstructionEditorVersion((version) => version + 1)
    setDiscardDialogOpen(false)
    if (pendingPlanTab != null) setSelectedPlanTab(pendingPlanTab)
    setPendingPlanTab(null)
    navigationBlocker.proceed?.()
  }
  const selectPlanTab = (tabIndex: number) => {
    if (tabIndex === selectedPlanTab) return
    if (isInstructionsDirty) {
      setPendingPlanTab(tabIndex)
      setDiscardDialogOpen(true)
      return
    }
    setSelectedPlanTab(tabIndex)
  }
  const breadcrumbItems = [
    {
      label: 'Planer',
      to: '/plans'
    },
    ...(plan ? getParentBreadcrumbs(plan) : []),
    {
      label: plan?.description ?? '',
      to: `/plans/${planId}`,
      icon: <FolderIcon />
    }
  ]
  const effectiveWorkspaceId = plan ? resolvePlanWorkspaceId(plan) : undefined
  const workspaceId = isWorkspaceFeatureEnabled
    ? effectiveWorkspaceId
    : undefined
  const purposeProperties: ScreenHeaderProperty[] = [
    {
      label: 'Hensikt',
      icon: <TargetIcon aria-hidden />,
      value: purposeText ?? 'Ikke angitt'
    },
    {
      label: 'OK-program',
      icon: <TargetIcon aria-hidden />,
      value: okProgramValue ?? '-'
    }
  ]
  const detailProperties: ScreenHeaderProperty[] = [
    {
      label: 'Opprettet av',
      icon: <UserIcon aria-hidden />,
      value: ownerName?.name ?? '-'
    },
    {
      label: 'Kontrakt',
      icon: <HandshakeIcon aria-hidden />,
      value: contract ? (
        <Link to={`/contracts/${contract.id}`}>{contract.title}</Link>
      ) : (
        '-'
      )
    },
    {
      label: 'Laboratorium',
      icon: <BuildingIcon aria-hidden />,
      value: lab?.name ?? '-'
    },
    {
      label: 'Referanse til lab',
      icon: <ClipboardTextIcon aria-hidden />,
      value: plan?.contractRef ?? 'Ikke angitt'
    }
  ]

  const isLoading =
    isPendingPlan ||
    isPendingContracts ||
    isPendingLabs ||
    isPendingPurposes ||
    isPendingSampleSummary
  const isError =
    (isErrorPlan && !plan) ||
    (isErrorContracts && !contracts) ||
    (isErrorLabs && !labs) ||
    (isErrorPurposes && !purposes) ||
    (isErrorSampleSummary && !sampleSummary)
  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      isLoading={isLoading}
      isError={isError}
      topBar={
        <TopBar
          logo={<AppLogo />}
          breadcrumbItems={breadcrumbItems}
          showNotificationBell={true}
        />
      }
      isFullWidth
    >
      {!isLoading && !isError && sampleSummary && (
        <ScreenLayout
          details={
            <Mtds.Grid data-gap='4'>
              <DetailsCard
                title='Hensikt'
                icon={<TargetIcon aria-hidden />}
                properties={purposeProperties}
              />
              <DetailsCard
                title='Detaljer'
                icon={<ClipboardTextIcon aria-hidden />}
                properties={detailProperties}
              />
            </Mtds.Grid>
          }
          mainMaxWidth={1000}
          header={
            <PlanScreenHeader
              key={plan.id}
              plan={plan}
              sampleSummary={sampleSummary}
              onClickCopy={() => {
                setModalOpen(true)
              }}
              onOpenCreateTaskModal={() => setCreateTaskModalOpen(true)}
              onOpenCreatePlanModal={() => setCreatePlanModalOpen(true)}
            />
          }
        >
          <Mtds.Tabs>
            <Mtds.Tabs.List>
              <Mtds.Tabs.Tab
                aria-selected={selectedPlanTab === 0}
                onClickCapture={(event) => {
                  if (isInstructionsDirty) {
                    event.preventDefault()
                    event.stopPropagation()
                    selectPlanTab(0)
                  }
                }}
                onClick={() => selectPlanTab(0)}
              >
                Oppgaver
              </Mtds.Tabs.Tab>
              <Mtds.Tabs.Tab
                aria-selected={selectedPlanTab === 2}
                onClickCapture={(event) => {
                  if (isInstructionsDirty) {
                    event.preventDefault()
                    event.stopPropagation()
                    selectPlanTab(2)
                  }
                }}
                onClick={() => selectPlanTab(2)}
              >
                Regelverk
              </Mtds.Tabs.Tab>
              <Mtds.Tabs.Tab
                aria-selected={selectedPlanTab === 1}
                onClickCapture={(event) => {
                  if (isInstructionsDirty) {
                    event.preventDefault()
                    event.stopPropagation()
                    selectPlanTab(1)
                  }
                }}
                onClick={() => selectPlanTab(1)}
              >
                Instruksjoner til inspektør
              </Mtds.Tabs.Tab>
              <Mtds.Tabs.Tab
                aria-selected={selectedPlanTab === 3}
                onClickCapture={(event) => {
                  if (isInstructionsDirty) {
                    event.preventDefault()
                    event.stopPropagation()
                    selectPlanTab(3)
                  }
                }}
                onClick={() => selectPlanTab(3)}
              >
                Statistikk
              </Mtds.Tabs.Tab>
            </Mtds.Tabs.List>
            <Mtds.Tabs.Panel hidden={selectedPlanTab !== 0}>
              <Mtds.Grid data-gap='8'>
                {!plan.tasks?.length && !plan.children?.length ? (
                  <EmptyState
                    onOpenCreateTaskModal={() => setCreateTaskModalOpen(true)}
                    onOpenCreatePlanModal={() => setCreatePlanModalOpen(true)}
                    plan={plan}
                  />
                ) : (
                  <>
                    {!!plan.children?.length && (
                      <PlansTab plans={plan.children} />
                    )}
                    {!!plan.tasks?.length && <TasksTab plan={plan} />}
                  </>
                )}
              </Mtds.Grid>
            </Mtds.Tabs.Panel>
            <Mtds.Tabs.Panel hidden={selectedPlanTab !== 2}>
              {selectedPlanTab === 2 && (
                <PlanLegislationTab key={plan.id} plan={plan} />
              )}
            </Mtds.Tabs.Panel>
            <Mtds.Tabs.Panel hidden={selectedPlanTab !== 1}>
              <PlanInstructionsTab
                key={`${plan.id}-${instructionEditorVersion}`}
                plan={plan}
                onDirtyChange={setInstructionsDirty}
              />
            </Mtds.Tabs.Panel>
            <Mtds.Tabs.Panel hidden={selectedPlanTab !== 3}>
              {selectedPlanTab === 3 && (
                <PlanStatisticsTab key={plan.id} plan={plan} />
              )}
            </Mtds.Tabs.Panel>
          </Mtds.Tabs>
        </ScreenLayout>
      )}
      {!!plan && (
        <>
          <CopyPlanModal
            isOpen={isModalOpen}
            setOpen={setModalOpen}
            plan={plan}
          />
          <CreateTaskModal
            isOpen={isCreateTaskModalOpen}
            setOpen={setCreateTaskModalOpen}
            plan={plan}
            debug={debugParam === 'true'}
          />
          <CreatePlanModal
            isOpen={isCreatePlanModalOpen}
            setOpen={setCreatePlanModalOpen}
            parentPlan={plan}
            workspaceId={workspaceId}
          />
        </>
      )}
      <ModalDialog
        id='unsaved-plan-instructions-dialog'
        aria-labelledby='unsaved-plan-instructions-heading'
        closedby='closerequest'
        open={isDiscardDialogOpen || navigationBlocker.state === 'blocked'}
        onCancel={(event) => {
          event.preventDefault()
          keepEditingInstructions()
        }}
      >
        <Mtds.Button
          command='close'
          commandfor='unsaved-plan-instructions-dialog'
          aria-label='Lukk'
          onClick={keepEditingInstructions}
        />
        <Mtds.Prose>
          <Mtds.Heading id='unsaved-plan-instructions-heading'>
            Forkast endringer?
          </Mtds.Heading>
          <p>
            Du har ulagrede endringer i instruksjonene. Vil du forkaste dem?
          </p>
          <Mtds.Flex>
            <Mtds.Button type='button' onClick={keepEditingInstructions}>
              Tilbake til redigering
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='button'
              onClick={discardInstructions}
            >
              Forkast endringer
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Prose>
      </ModalDialog>
    </AppLayout>
  )
}
